import { useInfiniteQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { queryKeys } from "@/shared/utils/queryKeys";
import { useDemoMode } from "@/pages/demo/DemoModeContext";

import type { ActionType, ActivityLogEntry, ActorType } from "@/shared/types/activityLog";

interface EdgeResponseEntry {
  id: number;
  created_at: string;
  estate_id: number;
  action_type: string;
  actor_type: string;
  user_id: number | null;
  admin_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown> | null;
  actor_name: string | null;
  actor_email: string | null;
}

interface ActivityLogCursor {
  created_at: string;
  id: number;
}

interface EdgeResponse {
  entries: EdgeResponseEntry[];
  next_cursor: ActivityLogCursor | null;
}

interface ActivityLogPage {
  entries: ActivityLogEntry[];
  nextCursor: ActivityLogCursor | null;
}

export function useEstateActivityLog(estateId: number | undefined, itemId?: number) {
  const { isDemoMode } = useDemoMode();

  return useInfiniteQuery({
    queryKey: [...queryKeys.estate.activityLog(estateId!), itemId ?? null],
    initialPageParam: null as ActivityLogCursor | null,
    queryFn: async ({ pageParam }): Promise<ActivityLogPage> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) return { entries: [], nextCursor: null };

      const { data, error } = await supabase.functions.invoke<EdgeResponse>("legal/activity-log", {
        body: {
          estate_id: estateId,
          cursor: pageParam,
          ...(itemId !== undefined ? { entity_id: itemId } : {}),
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;
      if (!data?.entries) return { entries: [], nextCursor: null };

      return {
        entries: data.entries.map((row) => ({
          id: row.id,
          created_at: row.created_at,
          estate_id: row.estate_id,
          action_type: row.action_type as ActionType,
          actor_type: row.actor_type as ActorType,
          user_id: row.user_id,
          admin_id: row.admin_id,
          entity_type: row.entity_type,
          entity_id: row.entity_id,
          metadata: row.metadata ?? {},
          actor_name: row.actor_name,
          actor_email: row.actor_email,
        })),
        nextCursor: data.next_cursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!estateId && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 30_000,
  });
}
